import type { IconCode } from "@components/Atoms/Icon/LucideIcon";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import openNewTab from "@core-ui/utils/openNewTab";
import { makeGitShareData } from "@ext/git/actions/Clone/logic/makeGitShareData";
import getRepUrl from "@ext/git/core/GitPathnameHandler/clone/logic/getRepUrl";
import t from "@ext/localization/locale/translate";
import SourceType from "@ext/storage/logic/SourceDataProvider/model/SourceType";
import getPartGitSourceDataByStorageName from "@ext/storage/logic/utils/getPartSourceDataByStorageName";
import { type MouseEvent, useMemo } from "react";

export const useOpenExternalGitSourceButton = (closeHandler: () => void) => {
	const { sourceName, linkPathname } = useCatalogPropsStore(
		(state) => ({ sourceName: state.data?.sourceName, linkPathname: state.data?.link?.pathname }),
		"shallow",
	);
	const gesUrl = WorkspaceService.current().enterprise?.gesUrl;
	const { sourceType } = getPartGitSourceDataByStorageName(sourceName);

	const githubIcon = SourceType.gitHub === sourceType ? "github" : undefined;
	const gitlabIcon = SourceType.gitLab === sourceType ? "gitlab" : undefined;
	const gitverseIcon = SourceType.gitVerse === sourceType ? "gitverse" : undefined;
	const giteaIcon = SourceType.gitea === sourceType ? "gitea" : undefined;

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	const gitButtonProps = useMemo(
		() => ({
			shouldRender: !!sourceType && !gesUrl,
			children: `${t("open-in.generic")} ${sourceType}`,
			startIcon: (gitlabIcon || githubIcon || gitverseIcon || giteaIcon) as IconCode,

			onClick: (e: MouseEvent<HTMLButtonElement>) => {
				e.preventDefault();

				const gitShareData = makeGitShareData(linkPathname);

				openNewTab(getRepUrl(gitShareData).href);
				closeHandler();
			},
		}),
		[sourceType, gesUrl, closeHandler, linkPathname],
	);

	return { gitButtonProps };
};
